import { knowledgeService } from './knowledgeService';
import {
  isTtqSupportedSubject,
  resolveTtqDockSubject,
  ttqService,
  type TtqSubjectCode,
} from './ttqService';
import type { SubjectCode } from '../utils/subjectConfig';
import {
  computeReviewBurdenReductionPercent,
  countUniqueOriginalParticles,
} from '../utils/ttqBurdenStats';

/**
 * Client-side cache for home TTQ burden cards (只需复习颗粒数 / 复习负担减少).
 * Primary invalidation is event-driven (品题会话 / 浓缩结果变更)；TTL 仅作兜底。
 */
const HOME_TTQ_BURDEN_TTL_MS = 24 * 60 * 60 * 1000;

export type HomeTtqBurdenStats = {
  subject: TtqSubjectCode;
  burdenReductionPercent: number | null;
  coveredOriginalCount: number;
  subjectParticleTotal: number | null;
};

const homeTtqBurdenCache = new Map<string, { expiresAt: number; stats: HomeTtqBurdenStats }>();
let homeTtqBurdenGeneration = 0;
const homeTtqBurdenListeners = new Set<() => void>();

function buildCacheKey(userId: string, subject: TtqSubjectCode) {
  return `${String(userId || '').trim()}:${subject}`;
}

function cloneHomeTtqBurdenStats(stats: HomeTtqBurdenStats): HomeTtqBurdenStats {
  return { ...stats };
}

function bumpHomeTtqBurdenGeneration() {
  homeTtqBurdenGeneration += 1;
  for (const listener of homeTtqBurdenListeners) {
    listener();
  }
}

export function getHomeTtqBurdenGeneration() {
  return homeTtqBurdenGeneration;
}

export function subscribeHomeTtqBurdenCache(listener: () => void) {
  homeTtqBurdenListeners.add(listener);
  return () => {
    homeTtqBurdenListeners.delete(listener);
  };
}

export function peekHomeTtqBurdenStats(
  userId: string,
  activeSubject: SubjectCode,
): HomeTtqBurdenStats | null {
  const subject = resolveTtqDockSubject(activeSubject);
  if (!isTtqSupportedSubject(subject)) {
    return {
      subject,
      burdenReductionPercent: null,
      coveredOriginalCount: 0,
      subjectParticleTotal: null,
    };
  }
  const cached = homeTtqBurdenCache.get(buildCacheKey(userId, subject));
  if (!cached || cached.expiresAt <= Date.now()) {
    return null;
  }
  return cloneHomeTtqBurdenStats(cached.stats);
}

export function invalidateHomeTtqBurdenStats(userId?: string, subject?: SubjectCode) {
  if (!userId) {
    if (homeTtqBurdenCache.size > 0) {
      homeTtqBurdenCache.clear();
      bumpHomeTtqBurdenGeneration();
    }
    return;
  }
  const normalizedUserId = String(userId).trim();
  let changed = false;
  if (!subject) {
    for (const key of [...homeTtqBurdenCache.keys()]) {
      if (key.startsWith(`${normalizedUserId}:`)) {
        homeTtqBurdenCache.delete(key);
        changed = true;
      }
    }
  } else {
    const dockSubject = resolveTtqDockSubject(subject);
    const key = buildCacheKey(normalizedUserId, dockSubject);
    if (homeTtqBurdenCache.has(key)) {
      homeTtqBurdenCache.delete(key);
      changed = true;
    }
  }
  if (changed) {
    bumpHomeTtqBurdenGeneration();
  }
}

export async function loadHomeTtqBurdenStats(
  userId: string,
  activeSubject: SubjectCode,
  options?: { forceRefresh?: boolean },
): Promise<HomeTtqBurdenStats> {
  const subject = resolveTtqDockSubject(activeSubject);
  if (!isTtqSupportedSubject(subject)) {
    return {
      subject,
      burdenReductionPercent: null,
      coveredOriginalCount: 0,
      subjectParticleTotal: null,
    };
  }

  const cacheKey = buildCacheKey(userId, subject);
  if (!options?.forceRefresh) {
    const cached = homeTtqBurdenCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cloneHomeTtqBurdenStats(cached.stats);
    }
  }

  const [sessions, particlePage] = await Promise.all([
    ttqService.listSessions(subject).catch(() => []),
    knowledgeService.listKnowledgeParticles({
      userId,
      subject,
      page: 1,
      pageSize: 1,
    }).catch(() => null),
  ]);

  const coveredOriginalCount = countUniqueOriginalParticles(sessions);
  const total = particlePage?.pagination?.total;
  const subjectParticleTotal = typeof total === 'number' && Number.isFinite(total) ? total : null;

  const stats: HomeTtqBurdenStats = {
    subject,
    burdenReductionPercent: computeReviewBurdenReductionPercent(
      coveredOriginalCount,
      subjectParticleTotal,
    ),
    coveredOriginalCount,
    subjectParticleTotal,
  };

  homeTtqBurdenCache.set(cacheKey, {
    expiresAt: Date.now() + HOME_TTQ_BURDEN_TTL_MS,
    stats: cloneHomeTtqBurdenStats(stats),
  });

  return stats;
}
